import { ClientMsgKind, Role } from '@infiltre/shared';
import { AnimatePresence, motion } from 'motion/react';
import { selectRoom, useMyPlayer } from '@/entities/room';
import { PlayerReadyRoster } from '@/features/game-round';
import { DossierReveal } from '@/features/standalone-handoff';
import { useGameStore, useSessionStore } from '@/shared/hooks/use-shallow-store';
import { SoundCue, useSound } from '@/shared/lib/audio';
import { useT } from '@/shared/lib/i18n';
import { mugshotForPlayer } from '@/shared/lib/mugshot';

const AUTO_REVEAL_DELAY_MS = 600;

/**
 * Server gates Reveal → Discuss on unanimous ack; `acked` is derived from
 * the room snapshot so reconnect lands on the roster, not the dossier.
 */
export const OnlineRevealOrchestrator = () => {
  const t = useT();
  const playSound = useSound();
  const room = useGameStore(selectRoom);
  const driver = useSessionStore((s) => s.driver);
  const identity = useSessionStore((s) => s.identity);
  const me = useMyPlayer();
  const privateAssignment = useGameStore((s) => s.privateAssignment);

  if (!room || !driver || !identity || !me) return null;

  const acked = room.revealAcknowledgedBy.includes(me.id);
  const readyCount = room.revealAcknowledgedBy.length;
  const total = room.players.length;
  const pending = Math.max(0, total - readyCount);

  return (
    <div className="flex h-full flex-col">
      <div className="relative min-h-0 flex-1">
        <AnimatePresence mode="wait">
          {acked ? (
            <motion.div
              key="roster"
              className="absolute inset-0"
              initial={{ opacity: 0, y: 12 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -12 }}
              transition={{ duration: 0.32, ease: 'easeOut' }}
            >
              <PlayerReadyRoster
                players={room.players}
                readyIds={room.revealAcknowledgedBy}
                eyebrow={t('online.reveal.waiting.eyebrow')}
                title={t('online.reveal.waiting.title')}
                descriptor={t('online.reveal.waiting.descriptor', { pending })}
                centerLabel={t('online.reveal.waiting.center_label')}
                selfId={me.id}
              />
            </motion.div>
          ) : (
            <motion.div
              key="dossier"
              className="absolute inset-0"
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0, y: -12 }}
              transition={{ duration: 0.32, ease: 'easeOut' }}
            >
              <DossierReveal
                key={`reveal-${me.id}-${room.round}`}
                playerName={me.name}
                playerOrder={me.order}
                playerId={me.id}
                playerMugshot={mugshotForPlayer(me)}
                role={privateAssignment?.role ?? me.role ?? Role.Flic}
                word={privateAssignment?.word ?? me.word ?? ''}
                autoRevealMs={AUTO_REVEAL_DELAY_MS}
                acknowledgeLabel={t('online.reveal.acknowledge')}
                onReveal={() => playSound(SoundCue.WordReveal)}
                onAcknowledge={() => {
                  playSound(SoundCue.AcknowledgeWord);
                  driver.send({
                    kind: ClientMsgKind.AcknowledgeWord,
                    actorPlayerId: identity.playerId,
                  });
                }}
              />
            </motion.div>
          )}
        </AnimatePresence>
      </div>
    </div>
  );
};
